import React, { useMemo } from 'react';
import { Pie } from 'react-chartjs-2';
import { useQuery } from '@tanstack/react-query';
import api from '../../api/axiosInstance';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ChartData, ChartOptions } from 'chart.js';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { BlockCounted } from '../blockCounted';
import { PeriodType } from '../periodToggle';

ChartJS.register(ArcElement, Tooltip, Legend);

export interface OperationData {
  id: number;
  amount: number;
  date: string;
}

interface RatioPieChartProps {
  period: PeriodType;
  from?: string;
  to?: string;
}

export const RatioPieChart: React.FC<RatioPieChartProps> = ({ period, from, to }) => {
  const navigate = useNavigate();

  const queryParams = useMemo(() => {
    if (period === 'custom') {
      return {
        from: from || undefined,
        to: to || undefined,
      };
    }

    return undefined;
  }, [period, from, to]);

  const { data: incomes = [] } = useQuery<OperationData[], Error>({
    queryKey: ['incomes', period, from, to],
    queryFn: () =>
      api
        .get('/incomes', {
          params: queryParams,
        })
        .then(r => r.data),
  });

  const { data: expenses = [] } = useQuery<OperationData[], Error>({
    queryKey: ['expenses', period, from, to],
    queryFn: () =>
      api
        .get('/expenses', {
          params: queryParams,
        })
        .then(r => r.data),
  });

  const now = useMemo(() => new Date(), []);

  const { totalInc, totalExp } = useMemo(() => {
    const currentYear = now.getFullYear();
    const prevYear = currentYear - 1;

    const inPeriod = (d: Date) => {
      if (d.getTime() > now.getTime()) return false;

      if (period === 'custom') {
        if (!from || !to) return false;

        const start = new Date(from);
        const end = new Date(to);

        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
          return false;
        }

        end.setHours(23, 59, 59, 999);
        return d.getTime() >= start.getTime() && d.getTime() <= end.getTime();
      }

      if (period === 'years') {
        const y = d.getFullYear();
        return y === currentYear || y === prevYear;
      }

      if (period === 'year') {
        return d.getFullYear() === currentYear;
      }

      if (period === 'month') {
        return d.getFullYear() === currentYear && d.getMonth() === now.getMonth();
      }

      const weekStart = new Date(now);
      const day = weekStart.getDay();
      const diff = day === 0 ? 6 : day - 1;
      weekStart.setDate(weekStart.getDate() - diff);
      weekStart.setHours(0, 0, 0, 0);

      return d.getTime() >= weekStart.getTime() && d.getTime() <= now.getTime();
    };

    const ti = incomes.reduce((sum: number, o: OperationData) => {
      const d = new Date(o.date);
      return inPeriod(d) ? sum + Number(o.amount) : sum;
    }, 0);

    const te = expenses.reduce((sum: number, o: OperationData) => {
      const d = new Date(o.date);
      return inPeriod(d) ? sum + Number(o.amount) : sum;
    }, 0);

    return { totalInc: ti, totalExp: te };
  }, [incomes, expenses, period, now, from, to]);

  const chartData: ChartData<'pie'> = useMemo(
    () => ({
      labels: ['Доходы', 'Расходы'],
      datasets: [
        {
          data: [totalInc, totalExp],
          backgroundColor: ['rgba(28,172,0,0.7)', 'rgba(255,0,0,0.7)'],
        },
      ],
    }),
    [totalInc, totalExp],
  );

  const options: ChartOptions<'pie'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: (_event, elements) => {
        if (!elements.length) return;

        const index = elements[0].index;
        const label = chartData.labels?.[index];

        if (label === 'Доходы') navigate('/incomes');
        else if (label === 'Расходы') navigate('/expenses');
      },
      onHover: (event, chartElement) => {
        const target = event.native?.target as HTMLCanvasElement;
        if (!target) return;
        target.style.cursor = chartElement.length ? 'pointer' : 'default';
      },
      plugins: {
        legend: {
          position: 'bottom',
        },
        tooltip: {
          callbacks: {
            label: ctx => {
              const v = Number(ctx.parsed as number);
              const sum = totalInc + totalExp;
              const percent = sum > 0 ? ((v / sum) * 100).toFixed(1) : '0.0';
              return `${ctx.label}: ${v} ₽ (${percent}%)`;
            },
          },
        },
      },
    }),
    [chartData.labels, navigate, totalInc, totalExp],
  );

  const hasData = totalInc > 0 || totalExp > 0;

  return (
    <div>
      <BlockCounted type="all" incomeData={[totalInc]} expenseData={[totalExp]} />

      {hasData ? (
        <Box
          sx={{
            width: '100%',
            height: { xs: 320, sm: 420, md: 520 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Pie data={chartData} options={options} />
        </Box>
      ) : (
        <div>Нет записей для построения графики</div>
      )}
    </div>
  );
};
